import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IoIosHelpCircleOutline } from 'react-icons/io';
import { MdCheckCircle } from 'react-icons/md';

export type TAccountProfileUsernameFieldProps = {
  name?: string;
  disabled?: boolean;
  isChecking?: boolean;
  isAvailable?: boolean;
};

export const AccountProfileUsernameField: FC<TAccountProfileUsernameFieldProps> = ({
  name = 'username',
  disabled,
  isChecking,
  isAvailable,
}) => {
  const { control } = useFormContext();

  return (
    <Box>
      <Typography variant="subtitle2" component="div" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        Username
        <Tooltip title="Your unique @handle. Shown next to your name in @mentions across the community.">
          <Box sx={{ display: 'flex', cursor: 'pointer', color: 'text.secondary' }}>
            <IoIosHelpCircleOutline size={18} />
          </Box>
        </Tooltip>
      </Typography>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const showAvailable = isAvailable && !fieldState.error;
          return (
            <TextField
              {...field}
              value={field.value ?? ''}
              onChange={e => field.onChange(e.target.value.toLowerCase())}
              fullWidth
              placeholder="username"
              disabled={disabled}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ||
                (isChecking
                  ? 'Checking availability…'
                  : showAvailable
                    ? 'Username is available'
                    : 'Lowercase letters, numbers, period and underscore.')
              }
              FormHelperTextProps={showAvailable ? { sx: { color: 'success.main' } } : undefined}
              InputProps={{
                startAdornment: <InputAdornment position="start">@</InputAdornment>,
                endAdornment: isChecking ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : showAvailable ? (
                  <InputAdornment position="end">
                    <MdCheckCircle color="#2e7d32" size={18} />
                  </InputAdornment>
                ) : null,
              }}
            />
          );
        }}
      />
    </Box>
  );
};
